import { useQuery, usePaginatedQuery } from 'convex/react';
import { api } from '../../api';

export type UserIdentity = 'parent' | 'child' | 'mentor' | 'mentee' | 'self-managed' | 'organization';

export interface UserWithIdentity {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  identity?: UserIdentity;
}

export interface GroupInfo {
  id: string;
  name: string;
  type: 'organization' | 'family' | 'chaver';
  ownerId: string;
}

export interface SupervisorInfo {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
}

export const useUserIdentity = () => {
  // NOTE: This Convex query is protected (requires authentication via Clerk).
  // This app currently has no frontend auth provider configured, so calling it
  // causes a runtime crash: "ConvexError: Not logged in".
  //
  // To keep the UI functional, we *skip* the query unless a Clerk session exists.
  // When auth is later wired up properly, this will start working automatically.
  const hasClerkSession =
    typeof window !== "undefined" && Boolean((window as any)?.Clerk?.session);

  // Fetch user groups using the users.get.me query (skip when not authenticated)
  const userGroups = useQuery(
    api.users.get.me as any,
    hasClerkSession ? (undefined as any) : ("skip" as any)
  ) as any[] | undefined;

  // Fetch all groups to get owner info - only if we have user groups
  const allGroups = usePaginatedQuery(
    api.groups.list.all,
    userGroups && userGroups.length > 0 ? {} : "skip",
    { initialNumItems: 50 }
  );

  // Fetch all users to find the owner's info - only if we have user groups
  const allUsers = usePaginatedQuery(
    api.users.list.all,
    userGroups && userGroups.length > 0 ? {} : "skip",
    { initialNumItems: 100 }
  );

  // Find the current user from the device serial
  // We'll match based on the group membership
  const currentUserGroup = userGroups?.[0];

  // Determine identity based on group type
  const getIdentityFromGroupType = (
    groupType: 'organization' | 'family' | 'chaver' | undefined,
    role: 'admin' | 'viewer'
  ): UserIdentity | undefined => {
    if (!groupType) return undefined;

    if (groupType === 'family') {
      return role === 'admin' ? 'parent' : 'child';
    }
    if (groupType === 'chaver') {
      return role === 'admin' ? 'mentor' : 'mentee';
    }
    if (groupType === 'organization') {
      return 'organization';
    }
    return 'self-managed';
  };

  const identity = currentUserGroup?.group
    ? getIdentityFromGroupType(currentUserGroup.group.type, currentUserGroup.type)
    : undefined;

  // Find the group owner (parent/mentor) if user is child/mentee
  const getSupervisorInfo = (): SupervisorInfo | null => {
    if (!identity || (identity !== 'child' && identity !== 'mentee')) {
      return null;
    }

    const groupId = currentUserGroup?.group?.id;
    if (!groupId || !allGroups.results || !allUsers.results) {
      return null;
    }

    // Find the group to get the owner
    const group = allGroups.results.find(g => g.group_id === groupId);
    if (!group) return null;

    // Find the owner user
    const owner = allUsers.results.find(u => u._id === group.owner);
    if (!owner) return null;

    return {
      firstName: owner.firstName,
      lastName: owner.lastName,
      email: owner.email,
      phone: owner.phone,
    };
  };

  const supervisorInfo = getSupervisorInfo();
  const supervisorLabel =
    identity === 'child' ? 'Parent' : identity === 'mentee' ? 'Mentor' : null;

  const isLoading =
    hasClerkSession &&
    (userGroups === undefined ||
      (identity &&
        (identity === 'child' || identity === 'mentee') &&
        (allGroups.status === 'LoadingFirstPage' ||
          allUsers.status === 'LoadingFirstPage')));

  return {
    identity,
    groupType: currentUserGroup?.group?.type,
    groupName: currentUserGroup?.group?.name,
    supervisorInfo,
    supervisorLabel,
    isLoading,
  };
};
